import { Button, Skeleton } from '@hive/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@hive/component/setting-components';
import {
  SubscriptionService,
  UserQuotaService,
  WorkspaceSubscriptionService,
} from '@hive/core/modules/cloud';
import { SubscriptionPlan, SubscriptionStatus } from '@hive/graphql';
import { useI18n } from '@hive/i18n';
import { track } from '@hive/track';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import { useCallback, useEffect } from 'react';

import type { SettingState } from '../../types';
import { BillingHistory } from './billing-history';
import { LifetimePlanCard } from './lifetime-plan-card';
import { PaymentMethod } from './payment-method';
import { ProPlanCard } from './pro-plan-card';
import * as styles from './style.css';

export const BillingSettings = ({
  onChangeSettingState,
}: {
  onChangeSettingState: (state: SettingState) => void;
}) => {
  const t = useI18n();

  return (
    <>
      <SettingHeader
        title={t['com.hive.payment.billing-setting.title']()}
        subtitle={t['com.hive.payment.billing-setting.subtitle']()}
      />
      <SettingWrapper
        title={t['com.hive.payment.billing-setting.information']()}
      >
        <SubscriptionSettings onChangeSettingState={onChangeSettingState} />
      </SettingWrapper>
      <SettingWrapper title={t['com.hive.payment.billing-setting.history']()}>
        <BillingHistory />
      </SettingWrapper>
    </>
  );
};

const SubscriptionSettings = ({
  onChangeSettingState,
}: {
  onChangeSettingState: (state: SettingState) => void;
}) => {
  const t = useI18n();
  const subscriptionService = useService(SubscriptionService);
  const userQuotaService = useService(UserQuotaService);

  useEffect(() => {
    subscriptionService.subscription.revalidate();
    subscriptionService.prices.revalidate();
    userQuotaService.quota.revalidate();
  }, [subscriptionService, userQuotaService]);

  // Check workspace subscription (for team plans)
  const workspaceSubscriptionService = useServiceOptional(
    WorkspaceSubscriptionService
  );
  const workspaceSubscription = useLiveData(
    workspaceSubscriptionService?.subscription.subscription$
  );
  const isTeamWorkspace = workspaceSubscription?.plan === SubscriptionPlan.Team;

  // Check if user has a lifetime plan
  const isLifetimePlan = useLiveData(userQuotaService.quota.isLifetimePlan$);
  const quotaPlanName = useLiveData(userQuotaService.quota.planName$);

  useEffect(() => {
    workspaceSubscriptionService?.subscription.revalidate();
  }, [workspaceSubscriptionService]);

  const teamSubscription = useLiveData(subscriptionService.subscription.pro$);

  const openPlans = useCallback(
    (scrollAnchor?: string) => {
      track.$.settingsPanel.billing.viewPlans();
      onChangeSettingState({
        activeTab: 'plans',
        scrollAnchor: scrollAnchor,
      });
    },
    [onChangeSettingState]
  );
  const gotoCloudPlansSetting = useCallback(
    () => openPlans('cloudPricingPlan'),
    [openPlans]
  );
  const gotoWorkspaceBilling = useCallback(() => {
    onChangeSettingState({
      activeTab: 'workspace:billing',
    });
  }, [onChangeSettingState]);

  // If user is in a team workspace, show a message to view workspace billing
  if (isTeamWorkspace && !teamSubscription) {
    return (
      <div className={styles.subscription}>
        <SettingRow
          name={
            t['com.hive.payment.billing-setting.team-workspace-notice']?.() ??
            'Team Workspace'
          }
          desc={
            t[
              'com.hive.payment.billing-setting.team-workspace-notice.desc'
            ]?.() ??
            'You are currently in a team workspace. Your billing is managed at the workspace level.'
          }
        >
          <Button variant="primary" onClick={gotoWorkspaceBilling}>
            {t['com.hive.payment.billing-setting.view-workspace-billing']?.() ??
              'View Workspace Billing'}
          </Button>
        </SettingRow>
      </div>
    );
  }

  // If user has a lifetime plan, show the lifetime plan card
  if (isLifetimePlan && quotaPlanName) {
    return (
      <div className={styles.subscription}>
        <LifetimePlanCard planName={quotaPlanName} />
      </div>
    );
  }

  return (
    <div className={styles.subscription}>
      {/* Team plan card */}
      {teamSubscription !== null ? (
        <ProPlanCard gotoCloudPlansSetting={gotoCloudPlansSetting} />
      ) : (
        <SubscriptionSettingSkeleton />
      )}

      {teamSubscription !== null ? (
        (teamSubscription?.status === SubscriptionStatus.Active ||
          teamSubscription?.status === SubscriptionStatus.Trialing) && (
          <PaymentMethod />
        )
      ) : (
        <SubscriptionSettingSkeleton />
      )}
    </div>
  );
};

const SubscriptionSettingSkeleton = () => {
  const t = useI18n();
  return (
    <SettingWrapper title={t['com.hive.payment.billing-setting.information']()}>
      <div className={styles.subscriptionSettingSkeleton}>
        <Skeleton variant="rounded" height="104px" />
        <Skeleton variant="rounded" height="46px" />
      </div>
    </SettingWrapper>
  );
};
